import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Lion } from '@/components/brand/Lion'
import { Button } from '@/components/ui/Button'
import { Input, Textarea } from '@/components/ui/Field'
import { cn } from '@/lib/cn'
import { formatDateLong, formatInstant, type IsoDate } from '@/lib/dates'
import { layoutRows, parseAnswers, parseQuestions, type Answers, type FileAnswer, type Question } from '@/lib/questionnaire'
import { supabase } from '@/lib/supabase'
import { QuestionnaireForm } from './QuestionnaireForm'

/**
 * The couple's questionnaire, /q/<token> (29 Oct 2026). No login: the link is
 * the key. Answers save as they type (a moment after they stop), so they can
 * come back to it; Submit sends it in and puts it on Jena's To do. With
 * ?preview=1 it is Jena's look: nothing saves.
 */

interface Loaded {
  name: string
  intro: string | null
  questions: Question[]
  answers: Answers
  completedAt: string | null
  couple: string
  eventDate: IsoDate | null
}

async function call(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data, error, response } = await supabase.functions.invoke<Record<string, unknown>>('questionnaire', { body })
  if (!error && data) return data
  const res = (error as { context?: Response } | null)?.context ?? response
  const parsed = (await res?.json().catch(() => null)) as { error?: string } | null
  throw new Error(parsed?.error ?? 'Something went wrong. Please try again, or tell Jenalee.')
}

function read(data: Record<string, unknown>): Loaded {
  const q = data.questionnaire as Record<string, unknown>
  return {
    name: String(q.name ?? ''),
    intro: (q.intro as string | null) ?? null,
    questions: parseQuestions(q.questions),
    answers: parseAnswers(q.answers),
    completedAt: (q.completedAt as string | null) ?? null,
    couple: String(q.couple ?? ''),
    eventDate: (q.eventDate as IsoDate | null) ?? null,
  }
}

/** Only the typed answers go up; file answers are kept by the server. */
const typed = (answers: Answers) =>
  Object.fromEntries(Object.entries(answers).filter(([, value]) => typeof value === 'string'))

export function QuestionnaireFillPage() {
  const { token = '' } = useParams()
  const [params] = useSearchParams()
  const preview = params.get('preview') === '1'
  const queryClient = useQueryClient()
  const key = ['questionnaire-fill', token]

  const loaded = useQuery({
    queryKey: key,
    queryFn: async () => read(await call({ token, preview })),
    retry: false,
    refetchOnWindowFocus: false,
  })

  const [answers, setAnswers] = useState<Answers>({})
  const [touched, setTouched] = useState(false)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const started = useRef(false)
  useEffect(() => {
    if (loaded.data && !started.current) {
      started.current = true
      setAnswers(loaded.data.answers)
    }
  }, [loaded.data])

  const save = useMutation({
    mutationFn: async (next: Answers) => read(await call({ token, save: { answers: typed(next) } })),
    onSuccess: () => setSavedAt(new Date()),
  })
  const submit = useMutation({
    mutationFn: async () => read(await call({ token, submit: { answers: typed(answers) } })),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
  })

  // Save a moment after they stop typing.
  useEffect(() => {
    if (!touched || preview) return
    const timer = window.setTimeout(() => save.mutate(answers), 1200)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, touched, preview])

  const setAnswer = (id: string, value: string) => {
    setTouched(true)
    setAnswers((current) => ({ ...current, [id]: value }))
  }
  const setFiles = (id: string, files: FileAnswer[]) => setAnswers((current) => ({ ...current, [id]: files }))

  const q = loaded.data

  return (
    <div data-register="portal" className="min-h-dvh bg-paper text-ink">
      <div className="mx-auto flex max-w-[44rem] flex-col gap-8 px-4 py-10 sm:px-6">
        <header className="flex items-center gap-3">
          <Lion size={28} />
          <div>
            <p className="label">De Leeuw Photo &amp; Film</p>
            <p className="text-[length:var(--text-sm)] text-taupe">
              {q?.couple || 'Your questionnaire'}
              {q?.eventDate ? ` · ${formatDateLong(q.eventDate)}` : ''}
            </p>
          </div>
        </header>

        {preview ? (
          <p className="card p-3 text-[length:var(--text-sm)] text-amber">This is your preview, as the couple sees it. Nothing you type here is saved.</p>
        ) : null}
        {loaded.isPending ? <p className="text-taupe">Opening your questionnaire…</p> : null}
        {loaded.isError ? <p className="text-clay">{loaded.error.message}</p> : null}

        {q?.completedAt ? (
          <>
            <p className="card p-4 text-[length:var(--text-sm)] text-sage">
              Thank you! You sent this in on {formatInstant(q.completedAt)}. If anything changes, just tell Jenalee.
            </p>
            <QuestionnaireForm name={q.name} intro={null} questions={q.questions} answers={q.answers} />
          </>
        ) : q ? (
          <form
            className="flex flex-col gap-7"
            onSubmit={(event) => {
              event.preventDefault()
              if (!preview) submit.mutate()
            }}
          >
            <div className="flex flex-col gap-3">
              <h1 className="text-[length:var(--text-xl)] font-semibold">{q.name}</h1>
              {q.intro ? <p className="whitespace-pre-line text-[length:var(--text-sm)] leading-relaxed">{q.intro}</p> : null}
            </div>

            {layoutRows(q.questions).map((row) => (
              <div key={row.map((question) => question.id).join('+')} className={cn('grid gap-7', row.length === 2 && 'sm:grid-cols-2')}>
                {row.map((question) => (
                  <FillQuestion
                    key={question.id}
                    token={token}
                    preview={preview}
                    question={question}
                    value={answers[question.id]}
                    onText={(value) => setAnswer(question.id, value)}
                    onFiles={(files) => setFiles(question.id, files)}
                  />
                ))}
              </div>
            ))}

            <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center gap-3 border-t bg-paper px-4 py-3 sm:-mx-6 sm:px-6">
              <Button type="submit" variant="primary" disabled={preview} loading={submit.isPending}>
                Submit
              </Button>
              <span className="text-[length:var(--text-2xs)] text-taupe">
                {preview
                  ? 'Preview: nothing is saved.'
                  : save.isPending
                    ? 'Saving…'
                    : save.isError
                      ? 'Your last change did not save. Check your connection; it will try again as you type.'
                      : savedAt
                        ? 'Saved. You can close this and come back later.'
                        : 'Your answers save as you go.'}
              </span>
              {submit.isError ? <p className="w-full text-[length:var(--text-sm)] text-clay">{submit.error.message}</p> : null}
            </div>
          </form>
        ) : null}
      </div>
    </div>
  )
}

function FillQuestion({
  token,
  preview,
  question,
  value,
  onText,
  onFiles,
}: {
  token: string
  preview: boolean
  question: Question
  value: Answers[string] | undefined
  onText: (value: string) => void
  onFiles: (files: FileAnswer[]) => void
}) {
  if (question.type === 'note') {
    return (
      <div className="flex flex-col gap-1">
        {question.label ? <p className="font-semibold">{question.label}</p> : null}
        {question.help ? <p className="whitespace-pre-line text-[length:var(--text-sm)]">{question.help}</p> : null}
      </div>
    )
  }
  const text = typeof value === 'string' ? value : ''
  const label = (
    <span className="text-[length:var(--text-sm)] font-semibold leading-snug">
      {question.label}
      {question.required ? <span className="text-clay"> *</span> : null}
    </span>
  )
  const help = question.help ? <span className="text-[length:var(--text-2xs)] text-taupe">{question.help}</span> : null

  if (question.type === 'choice') {
    return (
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2">{label}</legend>
        {help}
        {(question.options ?? []).map((option) => (
          <label key={option} className="flex cursor-pointer items-center gap-2.5 text-[length:var(--text-sm)]">
            <input
              type="radio"
              name={question.id}
              className="size-4 accent-[var(--color-accent)]"
              checked={text === option}
              onChange={() => onText(option)}
            />
            {option}
          </label>
        ))}
      </fieldset>
    )
  }

  if (question.type === 'file') {
    return (
      <div className="flex flex-col gap-2">
        {label}
        {help}
        <FileUploads token={token} preview={preview} question={question} files={Array.isArray(value) ? value : []} onFiles={onFiles} />
      </div>
    )
  }

  return (
    <label className="flex flex-col gap-2">
      {label}
      {help}
      {question.type === 'long' ? (
        <Textarea rows={4} value={text} onChange={(e) => onText(e.target.value)} />
      ) : (
        <Input value={text} onChange={(e) => onText(e.target.value)} />
      )}
    </label>
  )
}

function FileUploads({
  token,
  preview,
  question,
  files,
  onFiles,
}: {
  token: string
  preview: boolean
  question: Question
  files: FileAnswer[]
  onFiles: (files: FileAnswer[]) => void
}) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function add(list: FileList) {
    setBusy(true)
    setError(null)
    let latest = files
    try {
      for (const file of Array.from(list)) {
        const slot = (await call({ token, upload: { question: question.id, name: file.name, type: file.type, size: file.size } })) as {
          fileId: string
          path: string
          uploadToken: string
          contentType: string
        }
        // The type comes from the file's name on the server: a phone sometimes sends none.
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .uploadToSignedUrl(slot.path, slot.uploadToken, file, { contentType: slot.contentType })
        if (uploadError) throw new Error(`${file.name} did not upload. Please try again.`)
        const after = read(await call({ token, uploaded: { question: question.id, fileId: slot.fileId, name: file.name } }))
        const value = after.answers[question.id]
        latest = Array.isArray(value) ? value : latest
        onFiles(latest)
      }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  async function remove(file: FileAnswer) {
    setError(null)
    try {
      const after = read(await call({ token, removeFile: { question: question.id, fileId: file.id } }))
      const value = after.answers[question.id]
      onFiles(Array.isArray(value) ? value : [])
    } catch (e) {
      setError((e as Error).message)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {files.length ? (
        <ul className="flex flex-col gap-1 text-[length:var(--text-sm)]">
          {files.map((file) => (
            <li key={file.id} className="flex items-center justify-between gap-3 rounded-md bg-[var(--surface-card)] px-3 py-2">
              <span className="min-w-0 truncate">{file.name}</span>
              <Button type="button" size="sm" variant="ghost" onClick={() => void remove(file)}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      <input
        ref={input}
        type="file"
        multiple
        accept="image/*,.heic,.pdf"
        className="hidden"
        onChange={(e) => e.target.files?.length && void add(e.target.files)}
      />
      <div>
        <Button type="button" variant="secondary" disabled={preview} loading={busy} loadingLabel="Uploading" onClick={() => input.current?.click()}>
          Add photos
        </Button>
      </div>
      {error ? <p className="text-[length:var(--text-sm)] text-clay">{error}</p> : null}
    </div>
  )
}
